import { createSlice, CaseReducer, PayloadAction } from "@reduxjs/toolkit";
import {
    IAttendeeCountResponse,
    IParticipationSummary,
    ParticipationLoadError,
} from "@Interfaces";

interface IParticipationStore {
    /** The channel `data` and the error belong to; responses for others are ignored. */
    channelId: string | null;
    /** The only fetch whose response is applied; older and invalidated ones are dropped. */
    requestId: number | null;
    data: IParticipationSummary | null;
    isLoading: boolean;
    loadError: ParticipationLoadError | null;
    isSavingAttendeeCount: boolean;
    attendeeCountError: string | null;
}

const initialState: IParticipationStore = {
    channelId: null,
    requestId: null,
    data: null,
    isLoading: false,
    loadError: null,
    isSavingAttendeeCount: false,
    attendeeCountError: null,
};

const fetchParticipationStart: CaseReducer<
    IParticipationStore,
    PayloadAction<{ channelId: string; requestId: number }>
> = (state, { payload: { channelId, requestId } }) => {
    state.requestId = requestId;
    if (state.channelId !== channelId) {
        // Never show one event's numbers or errors under another event.
        state.channelId = channelId;
        state.data = null;
        state.isSavingAttendeeCount = false;
        state.attendeeCountError = null;
    }
    state.isLoading = true;
    state.loadError = null;
};

const fetchParticipationSuccess: CaseReducer<
    IParticipationStore,
    PayloadAction<{ requestId: number; summary: IParticipationSummary }>
> = (state, { payload }) => {
    if (payload.requestId !== state.requestId) return;
    state.data = payload.summary;
    state.isLoading = false;
    state.loadError = null;
};

const fetchParticipationFailure: CaseReducer<
    IParticipationStore,
    PayloadAction<{ requestId: number; error: ParticipationLoadError }>
> = (state, { payload }) => {
    if (payload.requestId !== state.requestId) return;
    state.isLoading = false;
    state.loadError = payload.error;
};

const setAttendeeCountStart: CaseReducer<IParticipationStore> = (state) => {
    state.isSavingAttendeeCount = true;
    state.attendeeCountError = null;
};

const setAttendeeCountSuccess: CaseReducer<
    IParticipationStore,
    PayloadAction<{ channelId: string; result: IAttendeeCountResponse }>
> = (state, { payload }) => {
    if (payload.channelId !== state.channelId) return;
    state.isSavingAttendeeCount = false;
    state.attendeeCountError = null;
    // A refresh still in flight may have read the old count; drop it.
    state.requestId = null;
    state.isLoading = false;
    // The PUT returns only these fields; keep the rest of the summary.
    if (state.data) {
        state.data.attendee_count = payload.result.attendee_count;
        state.data.respondents = payload.result.respondent_count;
    }
};

const setAttendeeCountFailure: CaseReducer<
    IParticipationStore,
    PayloadAction<{ channelId: string; message: string }>
> = (state, { payload }) => {
    if (payload.channelId !== state.channelId) return;
    state.isSavingAttendeeCount = false;
    state.attendeeCountError = payload.message;
};

const clearAttendeeCountError: CaseReducer<IParticipationStore> = (state) => {
    state.attendeeCountError = null;
};

const participationSlice = createSlice({
    name: "participation",
    initialState,
    reducers: {
        fetchParticipationStart,
        fetchParticipationSuccess,
        fetchParticipationFailure,
        setAttendeeCountStart,
        setAttendeeCountSuccess,
        setAttendeeCountFailure,
        clearAttendeeCountError,
    },
});

export const { name, actions } = participationSlice;

export default participationSlice.reducer;
